import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { IDSAlert, IDSButton, IDSBreadcrumbs, IDSCheckbox } from "@inera/ids-react";
import AttachmentUploadSection from "./AttachmentUploadSection";
import SubmissionSectionTreeWithComments from "./SubmissionSectionTreeWithComments";
import SectionNavigator from "./SectionNavigator";
import AutosaveIndicator from "../common/AutosaveIndicator";
import { useAutosave } from "../../hooks/useAutosave";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import { validateZipAttachment } from "../../utils/e2eAttachments";
import { buildAnswersPayload, extractAnswersFromSections } from "../../utils/e2eAnswers";
import {
  filterSectionsWithComments,
  getEditableQuestionIds,
  getSectionCommentSummaries,
} from "../../utils/e2eSectionComments";
import {
  downloadAttachment,
  deleteAttachment,
  getAttachments,
  getSubmissionComments,
  getUserSubmission,
  submitClarifiedSubmission,
  updateClarificationAnswers,
  uploadAttachment,
} from "../../services/E2EAPI";
import {
  fetchE2EUserProfilesByIds,
  getE2EUserProfileDetails as getUserProfileDetails,
  resolveE2EUserDisplayName,
} from "../../utils/e2eUserProfiles";
import {
  getReviewingDepartments,
  resolveReviewingDepartmentLabel,
} from "../../utils/e2eReviewingDepartment";

/* ─────────────────────────────────────────────────────────────
   Check if a comment is from a reviewer
───────────────────────────────────────────────────────────── */
function isReviewerComment(comment) {
  return (
    // The tester API masks reviewer authors behind their department name, which
    // makes the flag the only dependable marker of a reviewer comment.
    comment.authorMasked === true ||
    comment.commentType === "REVIEWER_COMMENT" ||
    comment.commentType === "reviewer" ||
    (comment.author && comment.author.toLowerCase().includes("reviewer"))
  );
}

/* ─────────────────────────────────────────────────────────────
   Find answer IDs that have reviewer comments but no user reply
───────────────────────────────────────────────────────────── */
function getUnansweredAnswerIds(allComments) {
  if (!allComments || allComments.length === 0) return [];

  // Group comments by answerId
  const byAnswer = {};
  for (const c of allComments) {
    const aid = c.answerId || c.answer_id;
    if (!aid) continue;
    if (!byAnswer[aid]) byAnswer[aid] = { reviewer: 0, user: 0 };
    if (isReviewerComment(c)) {
      byAnswer[aid].reviewer++;
    } else {
      byAnswer[aid].user++;
    }
  }

  // Return answerIds where reviewer commented but user hasn't replied
  return Object.entries(byAnswer)
    .filter(([, counts]) => counts.reviewer > 0 && counts.user === 0)
    .map(([aid]) => Number(aid));
}


/* ─────────────────────────────────────────────────────────────
   Status badge helper
───────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const styles = {
    IN_CLARIFY: { bg: "var(--ids-palette-attention-95)", color: "var(--ids-palette-attention-40)", label: "Förtydligande begärt" },
    SUBMITTED: { bg: "var(--ids-palette-information-95)", color: "var(--ids-palette-information-40)", label: "Inskickad" },
    DRAFT: { bg: "var(--ids-palette-neutral-95)", color: "var(--ids-palette-neutral-30)", label: "Utkast" },
    IN_REVIEW: { bg: "var(--ids-palette-success-95)", color: "var(--ids-palette-success-40)", label: "Under granskning" },
    APPROVED: { bg: "var(--ids-palette-success-95)", color: "var(--ids-palette-success-40)", label: "Godkänd" },
    REJECTED: { bg: "var(--ids-palette-error-95)", color: "var(--ids-palette-error-40)", label: "Avslagen" },
  };

  const s = styles[status] || { bg: "var(--ids-palette-neutral-95)", color: "var(--ids-palette-neutral-30)", label: status };

  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "0.85rem",
        fontWeight: 600,
        backgroundColor: s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
   Main Component – ClarifySubmission (User)
───────────────────────────────────────────────────────────── */
export default function ClarifySubmission() {
  const { submissionId : id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [allComments, setAllComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [userProfilesById, setUserProfilesById] = useState({});

  // Answers to the commented questions — editable while in clarification.
  const [answers, setAnswers] = useState({});
  const [showOnlyWithComments, setShowOnlyWithComments] = useState(true);
  const [saveError, setSaveError] = useState("");

  /* ── Fetch submission + comments ─────────────────────────── */
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError("");

        const data = await getUserSubmission(id);
        setSubmission(data);
        setAnswers(extractAnswersFromSections(data?.form?.sections || []));

        try {
          // Reviewers are represented by their department, so only the tester's
          // own identities need a profile lookup here.
          const relatedUserIds = [data?.userId, user?.id, user?.sub];
          const profiles = await fetchE2EUserProfilesByIds(relatedUserIds);
          setUserProfilesById(profiles);
        } catch (profileErr) {
          console.error("Failed to load user profiles:", profileErr);
        }

        try {
          const commentsData = await getSubmissionComments(id, false);
          setAllComments(commentsData || []);
        } catch (commentsErr) {
          console.error("Failed to load comments:", commentsErr);
        }

        try {
          const attachmentsData = await getAttachments(id, false);
          setAttachments(attachmentsData || []);
        } catch (attachmentsErr) {
          console.error("Failed to load attachments:", attachmentsErr);
        }
      } catch (err) {
        console.error("Failed to load submission", err);
        setError("Kunde inte ladda inlämningen.");
      } finally {
        setLoading(false);
      }
    }

    if (id && isAuthenticated) fetchData();
  }, [id, isAuthenticated, user?.id, user?.sub]);

  /* ── Derived: what may be edited, and where the comments are ── */
  const allSections = useMemo(() => submission?.form?.sections || [], [submission]);

  const editableQuestionIds = useMemo(
    () => getEditableQuestionIds(allSections, allComments),
    [allSections, allComments]
  );

  const visibleSections = useMemo(
    () => (showOnlyWithComments ? filterSectionsWithComments(allSections, allComments) : allSections),
    [showOnlyWithComments, allSections, allComments]
  );

  const sectionStatus = useMemo(
    () => getSectionCommentSummaries(visibleSections, allComments),
    [visibleSections, allComments]
  );

  const canEditAnswers =
    submission?.status === "IN_CLARIFY" && editableQuestionIds.size > 0;

  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  /* ── Persist the revised answers ─────────────────────────── */
  // Only the commented questions are sent — the server rejects anything else.
  const saveAnswers = useCallback(async () => {
    const editableAnswers = Object.fromEntries(
      Object.entries(answers).filter(([questionId]) =>
        editableQuestionIds.has(Number(questionId))
      )
    );

    return updateClarificationAnswers(
      id,
      { answers: buildAnswersPayload(editableAnswers) }
    );
  }, [answers, editableQuestionIds, id]);

  const autosaveStatus = useAutosave(saveAnswers, [answers], {
    delay: 10000,
    enabled: canEditAnswers && !!id,
  });

  /* ── Re-submit after clarification ────────────────── */

  async function refreshComments() {
    try {
      const commentsData = await getSubmissionComments(id, false);
      setAllComments(commentsData || []);
    } catch (err) {
      console.error("Failed to refresh comments:", err);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;

    setUploadError("");

    const validationError = validateZipAttachment(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    try {
      const uploaded = await uploadAttachment(id, file);
      setAttachments((prev) => [...prev, uploaded]);
    } catch (err) {
      console.error("Failed to upload attachment:", err);
      setUploadError("Kunde inte ladda upp filen.");
    }
  }

  async function handleDeleteAttachment(attachmentId) {
    try {
      await deleteAttachment(id, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error("Failed to delete attachment:", err);
      setUploadError("Kunde inte ta bort bilagan.");
    }
  }


  async function handleSaveAnswers() {
    try {
      setSubmitLoading(true);
      setSaveError("");

      await saveAnswers();

      // Answer ids can shift when new rows are created, so reload the tree and
      // the comment threads that hang off them.
      const data = await getUserSubmission(id);
      setSubmission(data);
      setAnswers(extractAnswersFromSections(data?.form?.sections || []));
      await refreshComments();
    } catch (err) {
      console.error("Failed to save clarified answers", err);
      setSaveError("Kunde inte spara ändringarna.");
    } finally {
      setSubmitLoading(false);
    }
  }

  async function handleResubmit() {
    try {
      setSubmitLoading(true);
      setSubmitError("");
      setSaveError("");

      if (canEditAnswers) {
        await saveAnswers();
      }

      await submitClarifiedSubmission(id);
      navigate("/e2e/tester/submissions");
    } catch (err) {
      console.error("Failed to resubmit", err);
      setSubmitError("Kunde inte skicka in förtydligandet.");
    } finally {
      setSubmitLoading(false);
    }
  }

  /* ── Guards ─────────────────────────────────────────────── */
  if (loading) return <p className="ids-mt-4">Laddar inlämning…</p>;
  if (error)
    return (
      <div className="ids-m-6"><main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
        <IDSAlert type="error"><p>{error}</p></IDSAlert>
      </main></div>
    );
  if (!submission || !submission.form)
    return <p className="ids-mt-4">Inlämningen saknar formulärdata.</p>;

  const { userId, status, submittedAt, reviewedAt, reviewComment, form } = submission;
  const { formTitle, version, description, sections = [] } = form;
  const submissionUserProfile = getUserProfileDetails(userId, userProfilesById);
  const submissionUserDisplayName =
    submissionUserProfile?.name || resolveE2EUserDisplayName(userId, userProfilesById);
  const submissionUserEmail = submissionUserProfile?.email || "—";
  const submissionUserOrganization = submissionUserProfile?.organizationName || "—";

  const currentAuthorIdentifier = user?.id || user?.sub || user?.email;
  const authorName = currentAuthorIdentifier
    ? resolveE2EUserDisplayName(currentAuthorIdentifier, userProfilesById)
    : resolveE2EUserDisplayName(userId, userProfilesById);
  const reviewingDepartments = getReviewingDepartments(submission);
  const isClarifyStatus = status === "IN_CLARIFY";

  /* ── Check for unanswered reviewer comments ────────────── */
  const unansweredAnswerIds = getUnansweredAnswerIds(allComments);
  const unansweredCount = unansweredAnswerIds.length;
  const canSubmit = isClarifyStatus && unansweredCount === 0;

  const reviewerCommentCount = allComments.filter((c) => isReviewerComment(c)).length;

  return (
    <div className="ids-m-6">
      <main
        className="ids-content ids-p-6"
        style={{ maxWidth: "1200px", margin: "2rem auto" }}
      >
        {/* Breadcrumbs */}
        <div className="ids-mb-6">
          <IDSBreadcrumbs lead="Du är här:">
            <Link to="/start">Start</Link>
            <Link to="/e2e/tester/submissions">Mina inlämningar</Link>
            <a>Förtydliga inlämning</a>
          </IDSBreadcrumbs>
        </div>

        <h2 className="ids-heading-l ids-mb-4">Förtydliga inlämning</h2>

        {/* Info banner when clarification is requested */}
        {isClarifyStatus && (
          <div className="ids-mb-6">
            <IDSAlert
              headline={<h2>Granskaren har begärt förtydligande</h2>}
              type="attention"
            >
              <p style={{ marginTop: "4px", marginBottom: 0 }}>
                Läs igenom kommentarerna nedan och svara på de frågor som
                granskaren ställt. Klicka på kommentarikonerna (
                <span style={{ color: "var(--ids-palette-attention-40)" }}>💬</span>) för att se och
                besvara kommentarer. När du är klar, klicka på{" "}
                <strong>"Skicka in förtydligande"</strong>.
              </p>
              {reviewerCommentCount > 0 && (
                <p style={{ marginTop: "8px", marginBottom: 0, fontWeight: 600 }}>
                  {reviewerCommentCount} kommentar
                  {reviewerCommentCount !== 1 ? "er" : ""} från granskaren.
                  {unansweredCount > 0 && (
                    <span style={{ color: "var(--ids-palette-error-40)" }}>
                      {" "}Du har {unansweredCount} obesvarad
                      {unansweredCount !== 1 ? "e" : ""} kommentar
                      {unansweredCount !== 1 ? "er" : ""} kvar att besvara.
                    </span>
                  )}
                </p>
              )}
            </IDSAlert>
          </div>
        )}

        {/* Reviewer feedback card */}
        {(reviewingDepartments.length > 0 || reviewedAt || reviewComment) && (
          <div className="ids-mb-4">
            <IDSAlert
              headline={<h3> Feedback från granskare</h3>}
              type ="error">
            <p className="ids-mb-2">
              <strong>Handläggs av:</strong> {resolveReviewingDepartmentLabel(submission)}
            </p>
            {reviewedAt && (
              <p className="ids-mb-2">
                <strong>Granskad:</strong> {formatDateTimeNoSeconds(reviewedAt)}
              </p>
            )}
            {reviewComment && (
              <div
                className="ids-mt-3 ids-p-3"
                style={{
                  backgroundColor: "var(--ids-palette-neutral-100)",
                  borderRadius: "4px",
                  border: "1px solid var(--ids-palette-primary-40)",
                }}
              >
                <p style={{ fontStyle: "italic", fontSize: "1rem", margin: 0 }}>
                  "{reviewComment}"
                </p>
              </div>
            )}
            </IDSAlert>
          </div>
        
        )}

        {/* Submission metadata */}
        <div className="ids-mb-6">
          <p className="ids-mb-2">
            <strong>Formulär:</strong> {formTitle} v{version}
          </p>
          {description && <p className="ids-mb-2">{description}</p>}
          <p className="ids-mb-2"><strong>Användare:</strong> {submissionUserDisplayName}</p>
          <p className="ids-mb-2"><strong>Mejladress:</strong> {submissionUserEmail}</p>
          <p className="ids-mb-2"><strong>Organisation:</strong> {submissionUserOrganization}</p>
          <p className="ids-mb-2">
            <strong>Status:</strong> <StatusBadge status={status} />
          </p>
          {submittedAt && (
            <p className="ids-mb-2">
              <strong>Inskickad:</strong>{" "}
              {formatDateTimeNoSeconds(submittedAt)}
            </p>
          )}
        </div>

        <hr className="ids-mb-6" />

        {/* Sections and questions */}
        {sections.length === 0 && <p>Inga sektioner hittades.</p>}

        <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <div
              className="ids-mb-4"
              style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "center" }}
            >
              <IDSCheckbox
                checked={showOnlyWithComments}
                onChange={() => setShowOnlyWithComments((v) => !v)}
              >
                Visa endast frågor med kommentarer
              </IDSCheckbox>
            </div>

            {canEditAnswers && (
              <p className="ids-mb-4" style={{ fontSize: "0.875rem", color: "var(--ids-palette-neutral-30)" }}>
                Frågorna som granskaren har kommenterat går att ändra. Ändringarna sparas
                automatiskt och när du skickar in förtydligandet.
              </p>
            )}

            {sections.length > 0 && visibleSections.length === 0 && (
              <p><em>Inga frågor med kommentarer.</em></p>
            )}

            <SubmissionSectionTreeWithComments
              sections={visibleSections}
              submissionId={id}
              submissionUserId={submission?.userId}
              authorName={authorName}
              allComments={allComments}
              onCommentAdded={refreshComments}
              requireExistingComments={true}
              editableQuestionIds={canEditAnswers ? editableQuestionIds : undefined}
              answers={answers}
              onAnswerChange={canEditAnswers ? handleAnswerChange : undefined}
            />

            {canEditAnswers && (
              <div
                className="ids-mt-4"
                style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}
              >
                <IDSButton secondary size="m" onClick={handleSaveAnswers} disabled={submitLoading}>
                  {submitLoading ? "Sparar…" : "Spara ändringar"}
                </IDSButton>
                <AutosaveIndicator status={autosaveStatus} />
              </div>
            )}

            {saveError && (
              <div className="ids-mt-3">
                <IDSAlert type="error"><p>{saveError}</p></IDSAlert>
              </div>
            )}
          </div>

          <SectionNavigator sectionStatus={sectionStatus} />
        </div>

        <AttachmentUploadSection
          onFileUpload={handleFileUpload}
          uploadError={uploadError}
          attachments={attachments}
          onDownloadAttachment={(att) => downloadAttachment(id, att.id, false)}
          onDeleteAttachment={handleDeleteAttachment}
        />

        <hr className="ids-mb-6" />

        {/* Action buttons */}
        <div
          className="ids-mb-6"
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {isClarifyStatus && (
            <>
              <IDSButton size="m" onClick={handleResubmit} disabled={submitLoading || !canSubmit}>
                {submitLoading ? "Skickar…" : "Skicka in förtydligande"}
              </IDSButton>
              {!canSubmit && unansweredCount > 0 && (
                <span style={{ color: "var(--ids-palette-attention-40)", fontSize: "0.875rem", fontWeight: 600 }}>
                  Besvara alla kommentarer från granskaren innan du skickar in.
                </span>
              )}
            </>
          )}

          <IDSButton secondary size="m" onClick={() => navigate("/e2e/tester/submissions")}>
            Tillbaka
          </IDSButton>

          {submitError && (
            <div className="ids-mt-3" style={{ width: "100%" }}>
              <IDSAlert type="error"><p>{submitError}</p></IDSAlert>
            </div>
          )}
        </div>
        <AutosaveIndicator status={autosaveStatus} fixed />
      </main>
    </div>
  );
}